import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Components, Logger, Models, Services } from "@sendum/core";
import classnames from "classnames";

import { VisibilityRootState } from "../store/VisibilityStore";
import * as VisibilityActions from "../store/VisibilityActions";
import { VisibilitySensor } from "./VisibilitySensor";

import classes from "./VisibilityMarkenSensors.module.scss";
import { MarkenDownloadBtn } from "./MarkenDownloadBtn/MarkenDownloadBtn";
import { AnyAction } from "redux";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityMarkenSensors");

interface IProps {
	children?: JSX.Element | JSX.Element[];
	maxActiveSensors: number;
}

export const VisibilityMarkenSensors = ({ children, maxActiveSensors }: IProps): JSX.Element => {
	const dispatch = useDispatch();

	const device = useSelector<VisibilityRootState, Models.Device | undefined>((state: VisibilityRootState) => state.Visibility.device, shallowEqual);
	const present = useSelector<VisibilityRootState, Models.State | undefined>((state: VisibilityRootState) => state.Visibility.present);
	const isUpdateDevicePresent = useSelector<VisibilityRootState, boolean | undefined>((state: VisibilityRootState) => state.Visibility.isUpdateDevicePresent, shallowEqual);
	const sensors = useSelector<VisibilityRootState, string[]>((state: VisibilityRootState) => state.Visibility.sensors, shallowEqual);
	const multiplePlaceComponent = useSelector<VisibilityRootState, "graph" | "reports" | "rules" | "alarms" | "map" | undefined>((state: VisibilityRootState) => state.Visibility.multiplePlaceComponent, shallowEqual);
	const selectedSensor = useSelector<VisibilityRootState, string | undefined>((state: VisibilityRootState) => state.Visibility.selectedSensor, shallowEqual);
	const pathRegexp = /^\/visibility\/(public|shared|notification)\//g;
	const showUserInfo = !pathRegexp.test(window.location.pathname);
	const historyLoading = useSelector<VisibilityRootState, Map<string, boolean>>((state: VisibilityRootState) => state.Visibility.historyLoading);

	const sensorsSet = React.useMemo(() => {
		if (!device) {
			return [];
		}

		return ["location", ...Array.from(device.getAttributesConfig().keys())];
	}, [device]);

	const isLoading: boolean = React.useMemo(() => {
		if (!device) {
			return false;
		}

		if (sensorsSet.length !== 0 && Array.from(historyLoading.keys()).length === 0) {
			return true;
		}

		return sensorsSet.some(sensor => historyLoading.get(`${device.id}#${device.shipment.id}#${sensor}`));
	}, [historyLoading, device, sensorsSet]);

	React.useEffect(() => {
		if (!isLoading && device) {
			dispatch(VisibilityActions.setIsUpdateDevicePresent(true) as unknown as AnyAction);
			dispatch(VisibilityActions.updatePresent(device.shipment.id, device.id, sensorsSet) as unknown as AnyAction);
		}
	}, [device, dispatch, isLoading, sensorsSet]);


	const sensorOnClickHandler = (sensorName: string): void => {
		if (device && device.getSensorsMultiSelectConfig()) {
			const selectedSensors = [...sensors];
			if (selectedSensors.includes(sensorName)) {
				const removalCount = 1;
				selectedSensors.splice(selectedSensors.indexOf(sensorName), removalCount);
			} else if (selectedSensors.length < maxActiveSensors) {
				selectedSensors.push(sensorName);
			} else {
				// TODO alert for user. User can't select more then 6 sensors in one time.
			}
			if (selectedSensor) {
				dispatch(VisibilityActions.resetSelectedSensor(selectedSensors));
			}
			dispatch(VisibilityActions.setSensors(selectedSensors));
		} else {
			dispatch(VisibilityActions.setSensors([sensorName]));
		}
	};

	const alarmsOnClickHandler = (): void => {
		dispatch(VisibilityActions.setMultiplePlaceComponent(multiplePlaceComponent !== "alarms" ? "alarms" : "map"));
	};

	const onClickHomeButtonHandler = (): void => {
		dispatch(VisibilityActions.resetSelectedSensor(device?.getSelectedSensorsConfig(maxActiveSensors) ?? []));
		Services.EventEmitter.emit("chart_resetZoom", {});
	};

	logger.debug(present && present["tempProbe"] && !isLoading && !isUpdateDevicePresent ? present["tempProbe"] as { value: boolean | number; epoch: number } : undefined);


	return (
		<div className={classes.sensors} data-testid="shipment-buttons-group">
			<div className={classes.sensors__grid}>
				{(!!children && children instanceof Array) && children.map(c => c)}
				{(!!children && !(children instanceof Array)) && children}

				<div className={classes.sensors__item} key={"home"}>
					<Components.DeviceInfoButton
						className={classes.sensors__itemContent} type={"marken"} label={"home"} onClick={onClickHomeButtonHandler} active={false} />
				</div>

				{!!device && Array.from(device.getAttributesConfig().keys()).map((keyName: string) => (
					<div className={classes.sensors__item} key={keyName}>
						<VisibilitySensor
							className={classes.sensors__itemContent}
							sensorData={present && present[keyName] && !isLoading && !isUpdateDevicePresent ? present[keyName] as { value: boolean | number; epoch: number } : undefined}
							sensorName={keyName}
							view={"marken"}
							onClick={sensorOnClickHandler}
							active={sensors.includes(keyName)}
							config={device?.getAttributesConfig().get(keyName)} />
					</div>))}

				{showUserInfo && (<div className={classes.sensors__item} key={"alarms"}>
					<Components.DeviceInfoButton
						className={classes.sensors__itemContent} onClick={alarmsOnClickHandler} type={"marken-alarms"} alarmsCount={device?.getActiveAlarms().length}
						active={multiplePlaceComponent === "alarms"} />
				</div>)}
				<div className={classnames(classes.sensors__item, classes.sensors__hidenItem)} key={"download"}>
					<MarkenDownloadBtn className={classnames(classes.sensors__itemContent)} showUserInfo={showUserInfo} />
				</div>
			</div>
		</div>
	);
};
